import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import GlassCard from "@/components/common/GlassCard";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const SECTION_TYPES = [
  { key: "hero", label: "Hero", icon: "Sparkles" },
  { key: "about", label: "About", icon: "Info" },
  { key: "services", label: "Services", icon: "Wrench" },
  { key: "why-choose", label: "Why Choose Us", icon: "Award" },
  { key: "process", label: "Process", icon: "ListOrdered" },
  { key: "challenges", label: "Challenges", icon: "Target" },
  { key: "portfolio", label: "Portfolio", icon: "Briefcase" },
  { key: "stats", label: "Stats", icon: "TrendingUp" },
  { key: "testimonials", label: "Testimonials", icon: "Quote" },
  { key: "industries", label: "Industries", icon: "Building2" },
  { key: "logos", label: "Client Logos", icon: "Image" },
  { key: "team", label: "Team", icon: "Users" },
  { key: "faq", label: "FAQ", icon: "HelpCircle" },
  { key: "blog", label: "Blog", icon: "FileText" },
  { key: "pricing", label: "Pricing", icon: "CreditCard" },
  { key: "careers", label: "Careers", icon: "Briefcase" },
  { key: "contact", label: "Contact Preview", icon: "Mail" },
  { key: "cta", label: "CTA", icon: "Zap" },
];

const uid = () => Math.random().toString(36).slice(2, 10);

export default function HomeBuilder() {
  const [page, setPage] = useState(null);
  const [preview, setPreview] = useState("desktop");
  const [selectedId, setSelectedId] = useState(null);
  const [saved, setSaved] = useState(true);

  const [dragIndex, setDragIndex] = useState(null);

  const load = async () => {
    const { data } = await api.get("/pages/home");
    setPage(data);
    setSelectedId(data.sections?.[0]?.id);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const onLeave = (e) => {
      if (saved) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [saved]);

  if (!page) return <div className="text-white/50">Loading…</div>;

  const setSections = (next) => { setSaved(false); setPage({ ...page, sections: next }); };

  const addSection = (type) => {
    const section = { id: uid(), type, visible: true, content: {} };
    setSections([...(page.sections || []), section]);
    setSelectedId(section.id);
  };
  const removeSection = (id) => setSections(page.sections.filter(s => s.id !== id));
  const toggleVisible = (id) => setSections(page.sections.map(s => s.id === id ? { ...s, visible: !s.visible } : s));
  const moveTo = (from, to) => {
    if (from === to || from < 0 || to < 0 || to >= page.sections.length) return;
    const items = [...page.sections];
    const [row] = items.splice(from, 1);
    items.splice(to, 0, row);
    setSections(items);
  };
  const updateContent = (id, key, val) =>
    setSections(page.sections.map(s => s.id === id ? { ...s, content: { ...s.content, [key]: val } } : s));

  const save = async () => {
    try {
      await api.patch("/pages/home", { sections: page.sections });
      setSaved(true);
      toast.success("Home page saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const selected = page.sections.find(s => s.id === selectedId);

  const previewWidth = preview === "desktop" ? "100%" : preview === "tablet" ? "768px" : "375px";

  return (
    <div>
      <PageHeader
        title="Home Page Builder"
        subtitle="Drag sections to reorder. Visible sections render in this order on the live homepage. Hero and CTA copy still come from Brand & Pages."
        actions={
          <>
            <div className="flex items-center rounded-lg glass p-0.5">
              {[
                { k: "desktop", i: "Monitor" }, { k: "tablet", i: "Tablet" }, { k: "mobile", i: "Smartphone" }
              ].map(({ k, i }) => {
                const I = Icons[i];
                return (
                  <button key={k} onClick={() => setPreview(k)}
                    className={`h-8 px-3 text-xs rounded-md flex items-center gap-1.5 ${preview === k ? "bg-white/10 text-white" : "text-white/50"}`}>
                    <I className="w-3.5 h-3.5" />
                  </button>
                );
              })}
            </div>
            <span className={`text-xs ${saved ? "text-emerald-300" : "text-amber-300"}`}>{saved ? "All changes saved" : "Unsaved changes"}</span><Button onClick={save} className="btn-gradient text-white border-0"><Icons.Save className="w-4 h-4 mr-1.5" /> Save</Button>
          </>
        }
      />

      <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr_320px] gap-4">
        {/* Block library */}
        <GlassCard className="p-4">
          <h3 className="text-xs uppercase tracking-wider text-white/50 mb-1">Section Blocks</h3><p className="text-[11px] text-white/40 mb-3">Add a section, select it, then use the inspector to edit its heading.</p>
          <div className="space-y-1.5">
            {SECTION_TYPES.map((t) => {
              const I = Icons[t.icon] || Icons.Circle;
              return (
                <button key={t.key} onClick={() => addSection(t.key)}
                  className="w-full flex items-center gap-2 px-3 h-9 rounded-lg glass hover:bg-white/[0.06] text-sm text-white/80 hover:text-white transition-colors">
                  <I className="w-4 h-4 text-white/60" />
                  <span>{t.label}</span>
                  <Icons.Plus className="w-3.5 h-3.5 text-white/40 ml-auto" />
                </button>
              );
            })}
          </div>
          <div className="divider my-4"></div>
          <h3 className="text-xs uppercase tracking-wider text-white/50 mb-2">Sections</h3>
          <p className="text-[11px] text-white/40 mb-2">Drag the handle to reorder.</p>
          <div className="space-y-1">
            {page.sections.map((s, idx) => (
              <div
                key={s.id}
                draggable
                onDragStart={() => setDragIndex(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => { if (dragIndex != null) moveTo(dragIndex, idx); setDragIndex(null); }}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs cursor-grab active:cursor-grabbing ${selectedId === s.id ? "bg-white/10" : "hover:bg-white/[0.03]"} ${dragIndex === idx ? "opacity-50" : ""}`}
              >
                <Icons.GripVertical className="w-3.5 h-3.5 text-white/35 shrink-0" aria-hidden="true" />
                <button onClick={() => setSelectedId(s.id)} className="flex-1 text-left text-white/80 capitalize">
                  {idx + 1}. {s.type}
                </button>
                <button onClick={() => moveTo(idx, idx - 1)} disabled={idx === 0} className="p-1 text-white/40 hover:text-white disabled:opacity-20" aria-label={`Move ${s.type} up`}><Icons.ChevronUp className="w-3 h-3" /></button>
                <button onClick={() => moveTo(idx, idx + 1)} disabled={idx === page.sections.length - 1} className="p-1 text-white/40 hover:text-white disabled:opacity-20" aria-label={`Move ${s.type} down`}><Icons.ChevronDown className="w-3 h-3" /></button>
                <button onClick={() => toggleVisible(s.id)} className="p-0.5 text-white/40 hover:text-white" aria-label={s.visible ? "Hide section" : "Show section"}>
                  {s.visible ? <Icons.Eye className="w-3.5 h-3.5" /> : <Icons.EyeOff className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => removeSection(s.id)} className="p-0.5 text-white/40 hover:text-red-300" aria-label={`Remove ${s.type}`}><Icons.Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </GlassCard>

        {/* Preview */}
        <GlassCard className="p-6 min-h-[600px]">
          <div className="mx-auto transition-all" style={{ maxWidth: previewWidth }}>
            {page.sections.filter(s => s.visible).map((s) => (
              <div key={s.id} onClick={() => setSelectedId(s.id)}
                className={`rounded-xl border border-white/10 mb-3 p-6 cursor-pointer transition-colors ${selectedId === s.id ? "bg-white/[0.06]" : "bg-white/[0.02] hover:bg-white/[0.04]"}`}>
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{s.type}</div>
                {s.type === "hero" ? (
                  <>
                    <h3 className="text-2xl font-semibold text-white">{s.content?.headline || "Hero headline"}</h3>
                    <p className="text-sm text-white/60 mt-2">{s.content?.subhead || "Subhead text"}</p>
                    <div className="mt-3 inline-block px-3 py-1.5 rounded-md btn-gradient text-xs text-white">{s.content?.cta_label || "Get started"}</div>
                  </>
                ) : s.type === "cta" ? (
                  <div className="text-center py-4">
                    <div className="text-lg font-medium text-white">{s.content?.headline || "Ready to talk?"}</div>
                    <div className="mt-3 inline-block px-3 py-1.5 rounded-md btn-gradient text-xs text-white">{s.content?.cta_label || "Contact us"}</div>
                  </div>
                ) : (
                  <div>
                    {s.content?.title && <div className="text-sm font-medium text-white mb-1">{s.content.title}</div>}
                    {s.content?.subtitle && <div className="text-xs text-white/50">{s.content.subtitle}</div>}
                    {!s.content?.title && <div className="text-sm text-white/50">Auto-populated from {s.type} module.</div>}
                  </div>
                )}
              </div>
            ))}
            {page.sections.length === 0 && (
              <div className="text-center text-white/40 py-24 text-sm">Add sections from the left panel</div>
            )}
          </div>
        </GlassCard>

        {/* Inspector */}
        <GlassCard className="p-4 h-fit">
          <h3 className="text-xs uppercase tracking-wider text-white/50 mb-3">Inspector</h3>
          {selected ? (
            <div className="space-y-3">
              <div className="text-sm text-white capitalize">{selected.type} section</div>
              {selected.type === "hero" && (
                <>
                  <div><Label className="text-xs text-white/50">Headline</Label>
                    <Input value={selected.content?.headline || ""} onChange={(e) => updateContent(selected.id, "headline", e.target.value)} className="bg-black/20 border-white/10 text-white mt-1" /></div>
                  <div><Label className="text-xs text-white/50">Subhead</Label>
                    <Textarea value={selected.content?.subhead || ""} onChange={(e) => updateContent(selected.id, "subhead", e.target.value)} className="bg-black/20 border-white/10 text-white mt-1" rows={2} /></div>
                  <div><Label className="text-xs text-white/50">CTA Label</Label>
                    <Input value={selected.content?.cta_label || ""} onChange={(e) => updateContent(selected.id, "cta_label", e.target.value)} className="bg-black/20 border-white/10 text-white mt-1" /></div>
                  <div><Label className="text-xs text-white/50">CTA URL</Label>
                    <Input value={selected.content?.cta_url || ""} onChange={(e) => updateContent(selected.id, "cta_url", e.target.value)} className="bg-black/20 border-white/10 text-white mt-1" /></div>
                </>
              )}
              {selected.type === "cta" && (
                <>
                  <div><Label className="text-xs text-white/50">Headline</Label>
                    <Input value={selected.content?.headline || ""} onChange={(e) => updateContent(selected.id, "headline", e.target.value)} className="bg-black/20 border-white/10 text-white mt-1" /></div>
                  <div><Label className="text-xs text-white/50">CTA Label</Label>
                    <Input value={selected.content?.cta_label || ""} onChange={(e) => updateContent(selected.id, "cta_label", e.target.value)} className="bg-black/20 border-white/10 text-white mt-1" /></div>
                </>
              )}
              {!["hero", "cta"].includes(selected.type) && (
                <>
                  <div>
                    <Label className="text-xs text-white/50">Section Title</Label>
                    <Input value={selected.content?.title || ""} onChange={(e) => updateContent(selected.id, "title", e.target.value)} className="bg-black/20 border-white/10 text-white mt-1" placeholder="e.g. Our Services" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/50">Section Subtitle</Label>
                    <Textarea value={selected.content?.subtitle || ""} onChange={(e) => updateContent(selected.id, "subtitle", e.target.value)} className="bg-black/20 border-white/10 text-white mt-1" rows={2} placeholder="Optional description" />
                  </div>
                  <p className="text-xs text-white/40">The actual content is managed in the {selected.type} module. These fields control the section heading.</p>
                </>
              )}
            </div>
          ) : (
            <p className="text-xs text-white/40">Select a section to configure.</p>
          )}
        </GlassCard>
      </div>
    </div>
  );
}
